/**
 * The UI2 icon vocabulary — the complete set of glyph names a schema author
 * may write into a uiSchema, and the only names a `ui2:` component may render.
 *
 * Names are **shape-based**, not role-based: `triangle-alert`, not `warning`.
 * Role → shape is decided in code (see `BANNER_MODE_ICONS`), so a component can
 * change which glyph represents a role without invalidating deployed schemas.
 *
 * Every name resolves to the same lucide glyph on NM, DW and MW. See
 * `.claude/docs/ui2-icons.md` for how to add one.
 */
export const UI2IconNames = {
  // Navigation
  CHEVRON_RIGHT: 'chevron-right',
  CHEVRON_LEFT: 'chevron-left',
  CHEVRON_DOWN: 'chevron-down',
  CHEVRON_UP: 'chevron-up',
  ARROW_RIGHT: 'arrow-right',
  ARROW_LEFT: 'arrow-left',
  EXTERNAL_LINK: 'external-link',

  // Actions
  X: 'x',
  CIRCLE_X: 'circle-x',
  CHECK: 'check',
  CIRCLE_CHECK: 'circle-check',
  PLUS: 'plus',
  PENCIL: 'pencil',
  TRASH: 'trash',
  DOWNLOAD: 'download',
  UPLOAD: 'upload',
  PRINTER: 'printer',
  SEARCH: 'search',
  FILTER: 'filter',
  REFRESH_CW: 'refresh-cw',
  ZOOM_IN: 'zoom-in',
  MAXIMIZE: 'maximize',
  SHARE: 'share',

  // Status
  TRIANGLE_ALERT: 'triangle-alert',
  CIRCLE_ALERT: 'circle-alert',
  INFO: 'info',
  CLOUD_OFF: 'cloud-off',
  LOCK: 'lock',
  EYE: 'eye',
  EYE_OFF: 'eye-off',

  // Time and place
  CALENDAR: 'calendar',
  CLOCK: 'clock',
  MAP_PIN: 'map-pin',
  LOCATE_FIXED: 'locate-fixed',

  // Capture and media
  CAMERA: 'camera',
  IMAGE: 'image',
  FILM: 'film',
  SCAN_BARCODE: 'scan-barcode',
  SCAN_QR_CODE: 'scan-qr-code',
  SCAN_TEXT: 'scan-text',
  QR_CODE: 'qr-code',

  // Files
  FILE: 'file',
  FILE_TEXT: 'file-text',
  FILE_ARCHIVE: 'file-archive',
  TABLE: 'table',
  PAPERCLIP: 'paperclip',

  // Domain
  TRUCK: 'truck',
  PACKAGE: 'package',
  USER: 'user',
} as const

export type UI2IconName = (typeof UI2IconNames)[keyof typeof UI2IconNames]

/** Every name in the vocabulary, in declaration order. */
export const UI2_ICON_NAMES: UI2IconName[] = Object.values(UI2IconNames)
